import {Directive, inject, signal} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {HttpErrorResponse} from "@angular/common/http";
import {first} from "rxjs/operators";
import {firstValueFrom} from "rxjs";
import {form, required, submit} from "@angular/forms/signals";
import {LOREN_ROUTES} from "../../../../shared/constants/loren-routes.constants";
import {Language} from "../../../../shared/enums/language.enum";
import {LorenBaseFeature} from "../../../../shared/base-components/loren-base-feature/loren-base-feature";
import {
    LorenAuthenticationService
} from "../../../../shared/services/loren-authentication-service/loren-authentication.service";
import {LorenUtilsService} from "../../../../shared/services/loren-utils-service/loren-utils.service";

interface LoginModel {
    username: string;
    password: string;
    rememberMe: boolean;
}

@Directive()
export abstract class LoginBase extends LorenBaseFeature {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly authenticationService = inject(LorenAuthenticationService);

    submitting = signal(false);
    loginFailureMessageKey = signal('');
    model = signal<LoginModel>({username: '', password: '', rememberMe: false});
    loginForm = form(this.model, (login) => {
        required(login.username);
        required(login.password);
    });

    constructor() {
        super();
        this.setLanguage();
    }

    async login(): Promise<void> {
        if (this.submitting()) return;
        this.submitting.set(true);

        try {
            await submit(this.loginForm, {
                action: async () => {
                    const {username, password} = this.model();
                    try {
                        await firstValueFrom(this.authenticationService.authenticate(username, password));
                        this.loginSuccess();
                    } catch (err) {
                        this.loginFail(err as HttpErrorResponse);
                    }
                }
            });
        } finally {
            this.submitting.set(false);
        }
    }

    private setLanguage() {
        const currentLanguage = LorenUtilsService.getLastSelectedLanguage();

        this.route.queryParams.pipe(first()).subscribe((params) => {
            if (params.lang && params.lang !== currentLanguage) {
                LorenUtilsService.updateLastSelectedLanguage(params.lang);
                location.reload();
            } else if (!currentLanguage) {
                const userLang = navigator.language;
                if (!userLang?.includes(Language.BG)) {
                    LorenUtilsService.updateLastSelectedLanguage(Language.EN);
                } else {
                    LorenUtilsService.updateLastSelectedLanguage(Language.BG);
                }

                location.reload();
            }
        });
    }

    private loginSuccess() {
        this.router.navigate([
            this.route.snapshot.queryParams.returnUrl || LOREN_ROUTES.DASHBOARD,
        ]);
    }

    private loginFail(err: HttpErrorResponse) {
        this.loginFailureMessageKey.set(
            err.status === 403
                ? "errors.inactive_account"
                : "errors.wrong_username_or_password"
        );
    }
}
